import * as XLSX from "xlsx";
import { ILinhaInformeFaturamento, IResultadoValidacao } from "../interfaces/ILinhaInformeFaturamento";

/**
 * Faz o parse da planilha de Informe de Faturamento (Fleet Solutions) no front.
 * Replica a lógica do legado json/gerenciar_emplacamentos_informe_fat_fleet_solutions.php:
 * - valida extensão xls/xlsx;
 * - extrai numero_carta de matriz[0][0] (entre "Carta:" e "Q");
 * - dados a partir da linha índice 5, considerando linhas com chassi (coluna 8).
 */
export async function parseInformeFaturamento(arquivo: File): Promise<IResultadoValidacao> {
  const extensao = (arquivo.name.split(".").pop() ?? "").toLowerCase();
  if (extensao !== "xls" && extensao !== "xlsx") {
    throw new Error("Extensão inválida. Aceitos xlsx e xls");
  }

  const buffer = await arquivo.arrayBuffer();
  const workbook = XLSX.read(buffer, { type: "array", cellDates: true });
  const worksheet = workbook.Sheets[workbook.SheetNames[0]];
  const matriz: any[][] = XLSX.utils.sheet_to_json(worksheet, {
    header: 1,
    raw: false,
    defval: "",
  });

  const numeroCarta = getStringBetween(String(matriz?.[0]?.[0] ?? ""), "Carta:", "Q").trim();

  const linhas: ILinhaInformeFaturamento[] = [];

  for (let i = 5; i < matriz.length; i++) {
    const linha = matriz[i] ?? [];
    const chassi = String(linha[8] ?? "").trim();

    if (chassi === "") {
      continue;
    }

    const valorNumerico = paraFloat(linha[11]);

    linhas.push({
      alienacao: textoOuVazio(linha[0]),
      finalPlaca: tratamentoPlaca(linha[1]),
      origem: textoOuVazio(linha[2]),
      cliente: tratamentoCliente(linha[5]),
      nf: textoOuVazio(linha[6]),
      emissao: textoOuVazio(linha[7]),
      chassi: textoOuVazio(linha[8]),
      modelo: textoOuVazio(linha[9]),
      cor: textoOuVazio(linha[10]),
      valor: valorNumerico.toFixed(2),
      valorExibicao: valorNumerico.toLocaleString("pt-BR", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }),
      anoFabricacao: textoOuVazio(linha[12]),
      anoModelo: textoOuVazio(linha[13]),
      chaveDanfe: textoOuVazio(linha[16]),
      uf: textoOuVazio(linha[17]),
      blindagem: textoOuVazio(linha[18]),
      placaVermelha: textoOuVazio(linha[19]),
      inova: normalizarInova(linha[20]),
      localLacre: textoOuVazio(linha[21]),
      telefone: textoOuVazio(linha[22]),
    });
  }

  return { numeroCarta, linhas };
}

function textoOuVazio(valor: any): string {
  return valor == null ? "" : String(valor).trim();
}

const VALORES_INOVA_SIM = ["SIM", "S"];
const VALORES_INOVA_NAO = ["NAO", "N"];

function normalizarInova(valor: any): "Sim" | "Não" {
  const texto = (valor == null ? "" : String(valor))
    .trim()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toUpperCase()
    .replace(/\s+/g, " ");

  if (texto === "" || VALORES_INOVA_SIM.includes(texto)) {
    return "Sim";
  }

  if (VALORES_INOVA_NAO.includes(texto) || texto.startsWith("NAO ")) {
    return "Não";
  }

  return /\bINOVA\b/.test(texto) ? "Sim" : "Não";
}

/** Equivale a tratamentoPlaca() do PHP. */
function tratamentoPlaca(placa: any): string {
  const valor = placa == null ? "" : String(placa);
  if (valor.includes("S/")) {
    const semEspaco = valor.replace(/ /g, "");
    return semEspaco.slice(0, 2) + " " + semEspaco.slice(2);
  }
  return valor ? valor : "";
}

/** Equivale a tratamentoCliente() do PHP. */
function tratamentoCliente(cliente: any): string {
  return cliente ? String(cliente).replace(/('|"|`|’)/g, "") : "";
}

/** Equivale a DetranPr::Get_String_Between(). */
function getStringBetween(texto: string, inicio: string, fim: string): string {
  const str = " " + texto;
  const ini = str.indexOf(inicio);
  if (ini <= 0) {
    return "";
  }
  const posInicial = ini + inicio.length;
  const posFinal = str.indexOf(fim, posInicial);
  if (posFinal === -1) {
    return "";
  }
  return str.substring(posInicial, posFinal);
}

/** Converte o valor (numérico, US ou BR) para float, equivalente a floatval do PHP sobre o valor da célula. */
function paraFloat(valor: any): number {
  if (typeof valor === "number") {
    return valor;
  }
  const limpo = String(valor ?? "").replace(/[^0-9.,-]/g, "");
  if (limpo.includes(",") && limpo.includes(".")) {
    return parseFloat(limpo.replace(/\./g, "").replace(",", ".")) || 0;
  }
  if (limpo.includes(",")) {
    return parseFloat(limpo.replace(",", ".")) || 0;
  }
  return parseFloat(limpo) || 0;
}
